import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { debugLogger } from '@/utils/debugLogger';
import { useLikeLimits } from '@/hooks/useLikeLimits';
import { toast } from 'sonner';

export const useSimpleFeedLike = () => {
  const { user } = useAuth();
  const { likeLimits, incrementLikeCount } = useLikeLimits();

  const likeProfile = useCallback(async (targetUserId: string): Promise<boolean> => {
    if (!user?.id) {
      debugLogger.error('SimpleFeedLike', 'User not authenticated');
      return false;
    }

    // Check daily like limits before proceeding
    if (!likeLimits.canLike) {
      const message = likeLimits.isUnlimited 
        ? 'Erro ao verificar limites de curtidas'
        : `Limite diário de curtidas atingido (${likeLimits.limit})`;
      
      toast.error(message);
      debugLogger.warn('SimpleFeedLike', 'Like blocked by daily limits', {
        current: likeLimits.current,
        limit: likeLimits.limit,
        canLike: likeLimits.canLike
      });
      return false;
    }
    
    try {
      debugLogger.debug('SimpleFeedLike', `Liking profile: ${targetUserId}`);
      
      // Check if there's an existing connection between these users (any direction)
      const { data: existingConnection, error: checkError } = await supabase
        .from('connections')
        .select('id, status, user_id, target_id, initiated_by')
        .or(`and(user_id.eq.${user.id},target_id.eq.${targetUserId}),and(user_id.eq.${targetUserId},target_id.eq.${user.id})`)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        debugLogger.error('SimpleFeedLike', 'Error checking existing connection:', checkError);
        return false;
      }

      if (existingConnection) {
        debugLogger.debug('SimpleFeedLike', 'Found existing connection:', existingConnection);
        
        // If connection already matched, nothing to do
        if (existingConnection.status === 'matched') {
          debugLogger.debug('SimpleFeedLike', 'Connection already matched');
          return true;
        }
        
        // If connection exists and is 'sent', this is a "like back" - create match
        if (existingConnection.status === 'sent') {
          // Check who initiated the original connection
          const isCurrentUserLikingBack = existingConnection.initiated_by !== user.id;
          
          if (isCurrentUserLikingBack) {
            debugLogger.debug('SimpleFeedLike', 'Creating match - updating existing connection to matched');
            
            // Update the existing connection to 'matched'
            const { error: updateError } = await supabase
              .from('connections')
              .update({ status: 'matched' })
              .eq('id', existingConnection.id);

            if (updateError) {
              debugLogger.error('SimpleFeedLike', 'Error updating existing connection to matched:', updateError);
              return false;
            }

            debugLogger.debug('SimpleFeedLike', 'Match created successfully');
            // Don't increment like count for matches (accepting a like)
            return true;
          } else {
            // Current user already liked this person
            debugLogger.debug('SimpleFeedLike', 'Current user already liked this profile');
            return true;
          }
        }
      }

      // No existing connection - create new 'sent' connection
      debugLogger.debug('SimpleFeedLike', 'Creating new connection');
      
      const { error: insertError } = await supabase
        .from('connections')
        .insert({
          user_id: user.id,
          target_id: targetUserId,
          status: 'sent',
          initiated_by: user.id
        });

      if (insertError) {
        // If it's a duplicate key error, consider it successful (race condition)
        if (insertError.code === '23505') {
          debugLogger.debug('SimpleFeedLike', 'Connection already exists (race condition), considering successful');
          return true;
        }
        debugLogger.error('SimpleFeedLike', 'Error creating new connection:', insertError);
        return false;
      }

      // Increment like count after successful like
      incrementLikeCount();

      debugLogger.debug('SimpleFeedLike', 'Profile liked successfully');
      return true;
    } catch (error) {
      debugLogger.error('SimpleFeedLike', 'Error liking profile:', error);
      return false;
    }
  }, [user, likeLimits, incrementLikeCount]);

  const unlikeProfile = useCallback(async (targetUserId: string): Promise<boolean> => {
    if (!user?.id) {
      debugLogger.error('SimpleFeedLike', 'User not authenticated');
      return false;
    }
    
    try {
      debugLogger.debug('SimpleFeedLike', `Unliking profile: ${targetUserId}`);
      
      // Find connection where current user initiated the like
      const { error: deleteError, count } = await supabase
        .from('connections')
        .delete({ count: 'exact' })
        .eq('user_id', user.id)
        .eq('target_id', targetUserId)
        .eq('initiated_by', user.id)
        .eq('status', 'sent');

      if (deleteError) {
        debugLogger.error('SimpleFeedLike', 'Error unliking profile:', deleteError);
        return false;
      }

      debugLogger.debug('SimpleFeedLike', 'Profile unliked successfully', {
        deletedRows: count
      });

      return (count || 0) > 0;
    } catch (error) {
      debugLogger.error('SimpleFeedLike', 'Error unliking profile:', error);
      return false;
    }
  }, [user]);

  return {
    likeProfile,
    unlikeProfile,
    likeLimits
  };
};
